/**
 * ErrorFallback Component
 *
 * Shared error display UI used by both ErrorBoundary and FeatureErrorBoundary.
 * Shows auto-reporting status when Sentry is configured.
 */

import type { JSX, ChangeEvent } from 'react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Icon } from '@/components/ui/icon';
import { isSentryConfigured } from '@/lib/sentry';
import { buildDiagnosticsEmailPayload } from '@/lib/diagnosticsReport';
import { breadcrumbs } from '@/lib/breadcrumbs';

interface ErrorFallbackProps {
  /** Display variant */
  variant: 'inline' | 'full-page';
  /** Error title */
  title: string;
  /** Error description */
  message: string;
  /** Unique error identifier */
  errorId?: string;
  /** Error timestamp (ISO string) */
  errorTimestamp?: string;
  /** Original error for diagnostics */
  error?: Error;
  /** Retry handler */
  onRetry?: () => void;
  /** Number of retries attempted */
  retryCount?: number;
  /** Max retries allowed */
  maxRetries?: number;
  /** Close/dismiss handler */
  onClose?: () => void;
  /** Custom close button label */
  closeLabel?: string;
  /** Reload handler (full-page only) */
  onReload?: () => void;
  /** Whether error was sent to Sentry */
  sentryReported?: boolean;
}

export function ErrorFallback({
  variant,
  title,
  message,
  errorId,
  errorTimestamp,
  error,
  onRetry,
  retryCount = 0,
  maxRetries = 3,
  onClose,
  closeLabel = 'Chiudi',
  onReload,
  sentryReported,
}: ErrorFallbackProps): JSX.Element {
  const [showReportForm, setShowReportForm] = useState(false);
  const [userDescription, setUserDescription] = useState('');

  const isAutoReported = sentryReported ?? isSentryConfigured();
  const canRetry = onRetry && retryCount < maxRetries;

  const handleDescriptionChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setUserDescription(e.target.value);
  };

  const handleSendReport = () => {
    const payload = buildDiagnosticsEmailPayload(userDescription, {
      errorId,
      message: error?.message ?? 'Errore sconosciuto',
      timestamp: errorTimestamp,
      url: window.location.href,
      appVersion: import.meta.env.VITE_APP_VERSION,
      userDescription,
      recentActions: breadcrumbs.formatForReport(10),
    });

    const mailtoUrl = `mailto:?subject=${encodeURIComponent(payload.subject)}&body=${encodeURIComponent(payload.body)}`;

    try {
      window.location.href = mailtoUrl;
    } catch {
      navigator.clipboard
        .writeText(`${payload.subject}\n\n${payload.body}`)
        .then(() => {
          alert("Diagnostica copiata negli appunti. Puoi incollarla in un'email.");
        })
        .catch(() => {
          alert("Impossibile aprire il client email. Contatta l'assistenza.");
        });
    }
  };

  // Shared card content - used by both variants
  const cardContent = (
    <>
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 text-danger-500">
          <Icon name="error" className={variant === 'full-page' ? 'icon-lg' : 'icon-md'} />
        </div>
        <div className="flex-1 min-w-0">
          <h1
            className={`font-semibold text-text-primary ${variant === 'full-page' ? 'text-lg' : 'text-sm'}`}
          >
            {title}
          </h1>
          <p className="text-sm text-text-secondary mt-1">{message}</p>
        </div>
      </div>

      {/* Error details */}
      <div className="rounded-md bg-surface-secondary p-3 mt-4">
        {errorId && <p className="text-xs text-text-secondary font-mono mb-1">ID: {errorId}</p>}
        {errorTimestamp && (
          <p className="text-xs text-text-secondary">
            {new Date(errorTimestamp).toLocaleString('it-IT')}
          </p>
        )}
        {error?.message && (
          <p className="text-xs text-danger-500 mt-2 break-all">{error.message}</p>
        )}
      </div>

      {/* Auto-report status */}
      <div className="mt-4">
        <AutoReportStatus
          isAutoReported={isAutoReported}
          showReportForm={showReportForm}
          onToggleReportForm={() => setShowReportForm(!showReportForm)}
        />
      </div>

      {/* Report form */}
      {showReportForm && (
        <ReportForm
          userDescription={userDescription}
          onDescriptionChange={handleDescriptionChange}
          onSend={handleSendReport}
          isAutoReported={isAutoReported}
        />
      )}

      {/* Actions */}
      <div className={`mt-4 flex ${variant === 'full-page' ? 'flex-col' : 'flex-wrap'} gap-2`}>
        {canRetry && (
          <Button
            onClick={onRetry}
            size={variant === 'full-page' ? 'md' : 'sm'}
            className={variant === 'full-page' ? 'w-full' : ''}
          >
            Riprova
            {retryCount > 0 && ` (${retryCount}/${maxRetries})`}
          </Button>
        )}

        {onReload && (
          <Button
            onClick={onReload}
            variant="secondary"
            size={variant === 'full-page' ? 'md' : 'sm'}
            className={variant === 'full-page' ? 'w-full' : ''}
          >
            Ricarica pagina
          </Button>
        )}

        {onClose && (
          <Button
            variant="outline"
            size={variant === 'full-page' ? 'md' : 'sm'}
            onClick={onClose}
            className={variant === 'full-page' ? 'w-full' : ''}
          >
            {closeLabel}
          </Button>
        )}
      </div>

      {/* Dev details */}
      {import.meta.env.DEV && error?.stack && (
        <details className="mt-4">
          <summary className="cursor-pointer text-xs text-text-secondary hover:text-text-primary">
            Dettagli errore (sviluppo)
          </summary>
          <pre className="mt-2 max-h-32 overflow-auto whitespace-pre-wrap rounded bg-surface-secondary p-2 text-xs text-text-secondary">
            {error.stack}
          </pre>
        </details>
      )}
    </>
  );

  // Full-page variant: centered on screen
  if (variant === 'full-page') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-secondary p-4">
        <div className="max-w-md w-full rounded-lg border border-[var(--border-default)] bg-surface p-6 shadow-lg">
          {cardContent}
        </div>
      </div>
    );
  }

  // Inline variant: same card style, just not centered
  return (
    <div className="rounded-lg border border-[var(--border-default)] bg-surface p-4 shadow-sm">
      {cardContent}
    </div>
  );
}

// ----- Internal Components -----

interface AutoReportStatusProps {
  isAutoReported: boolean;
  showReportForm: boolean;
  onToggleReportForm: () => void;
}

function AutoReportStatus({
  isAutoReported,
  showReportForm,
  onToggleReportForm,
}: AutoReportStatusProps): JSX.Element {
  if (isAutoReported) {
    return (
      <div className="rounded-md bg-success-100 p-3">
        <div className="flex items-center gap-2 text-sm">
          <Icon name="check_circle" className="icon-base text-success-500" />
          <span className="font-medium text-success-500">Errore segnalato automaticamente</span>
        </div>
        <p className="text-xs text-text-secondary mt-1 ml-6">
          Il problema è stato registrato. Non è necessario fare altro.
        </p>
        {!showReportForm && (
          <button
            type="button"
            onClick={onToggleReportForm}
            className="text-xs text-brand-blue-600 hover:text-brand-blue-700 underline mt-2 ml-6"
          >
            Vuoi aggiungere dettagli? (opzionale)
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="text-sm text-text-secondary">
      {!showReportForm ? (
        <button
          type="button"
          onClick={onToggleReportForm}
          className="text-brand-blue-600 hover:text-brand-blue-700 underline"
        >
          Segnala problema via email
        </button>
      ) : (
        <span>Compila il form per segnalare il problema</span>
      )}
    </div>
  );
}

interface ReportFormProps {
  userDescription: string;
  onDescriptionChange: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  onSend: () => void;
  isAutoReported: boolean;
}

function ReportForm({
  userDescription,
  onDescriptionChange,
  onSend,
  isAutoReported,
}: ReportFormProps): JSX.Element {
  return (
    <div className="mt-4 space-y-2">
      <label htmlFor="error-description" className="text-sm font-medium text-text-secondary block">
        {isAutoReported
          ? 'Informazioni aggiuntive (opzionale)'
          : "Cosa stavi facendo quando è apparso l'errore?"}
      </label>
      <Textarea
        id="error-description"
        value={userDescription}
        onChange={onDescriptionChange}
        placeholder="Es: Stavo aprendo il menu a tendina per selezionare la frequenza..."
        className="text-sm bg-white border-neutral-300"
        rows={3}
      />
      <Button variant="primary" size="sm" onClick={onSend}>
        {isAutoReported ? 'Invia dettagli aggiuntivi' : 'Invia segnalazione'}
      </Button>
    </div>
  );
}
